import DOMPurify from 'dompurify'

type UTMParams = {
  campaign?: string | null
  source?: string | null
  medium?: string | null
  content?: string | null
  placement?: string | null
}

const STORED_PARAMS = ['campaign', 'source', 'medium', 'content', 'placement']
const UTM_KEYS: Record<string, keyof UTMParams> = {
  utm_campaign: 'campaign',
  utm_source: 'source',
  utm_medium: 'medium',
  utm_content: 'content',
  mtm_placement: 'placement'
}

/**
 * Record UTM and Matomo Martketing codes into sessionStorage
 */
export const setStoredUTMParams = (location: Location): void => {
  const searchParams = new URLSearchParams(location?.search)

  for (const param of STORED_PARAMS) {
    const utmValue = searchParams.get(`utm_${param}`)
    const mtmValue = searchParams.get(`mtm_${param}`)
    const value = utmValue ?? mtmValue

    if (value && value.trim() !== '') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const sanitizedValue: string = DOMPurify.sanitize(value)
      sessionStorage.setItem(param, encodeURIComponent(sanitizedValue))
    }
  }
}

/**
 * Get UTM and Matomo Martketing codes from sessionStorage
 */

export const buildUTMTrackingUrl = (
  baseUrl: string,
  additionalParams: Record<string, string> = {}
): string => {
  try {
    const url = new URL(baseUrl, 'https://dummy.com')
    const utmParams = getStoredUTMParams()

    for (const [key, field] of Object.entries(UTM_KEYS)) {
      const value = utmParams[field]
      if (!url.searchParams.has(key) && value) {
        url.searchParams.set(key, value)
      }
    }

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value)
    })

    return url.pathname + url.search + url.hash
  } catch (error) {
    console.warn('Error building UTM URL:', error)
    return baseUrl
  }
}

export const getStoredUTMParams = (): UTMParams => {
  const getDecodedValue = (key: string): string | null => {
    const encodedValue = sessionStorage.getItem(key)
    if (!encodedValue) return null
    try {
      return decodeURIComponent(encodedValue)
    } catch (error) {
      console.warn(`Error decoding UTM parameter ${key}:`, error)
      return encodedValue // Fallback al valor original si no se puede decodificar
    }
  }

  const stored = STORED_PARAMS.reduce<Record<string, string | null>>(
    (acc, key) => {
      acc[key] = getDecodedValue(key)
      return acc
    },
    {}
  )

  return {
    campaign: stored.campaign,
    source: stored.source,
    medium: stored.medium,
    content: stored.content,
    placement: stored.placement || null
  }
}
